import { useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { _t } from '@/i18n/t';

type Confirming = 'startOver' | 'cancel' | null;

interface EscapeCancelCopy {
  link: string;
  note: string;
  confirm: string;
  keep: string;
}

interface Props {
  onStartOver?: () => void;
  onCancel?: () => void;
  /** Rescan is a different surface with its own wording; onboarding uses the default. */
  cancelCopy?: EscapeCancelCopy;
}

function Separator() {
  return <span className="ob-escape__sep" aria-hidden="true">·</span>;
}

type EscapeVariant = 'chip' | 'quiet-danger' | 'confirm' | 'confirm-danger' | 'keep';

function EscapeLink({
  label,
  onClick,
  variant,
  testId,
}: {
  label: string;
  onClick: () => void;
  variant: EscapeVariant;
  testId: string;
}) {
  return (
    <button
      type="button"
      /* design-system.css strips border and background from any button inside
         .th that does not opt out, which would erase the chip outline. */
      data-th-button="true"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'ob-escape__link',
        variant === 'chip' && 'ob-escape__link--chip text-muted-foreground hover:text-foreground',
        variant === 'quiet-danger' && 'ob-escape__link--quiet-danger',
        variant === 'confirm' && 'ob-escape__link--confirm text-foreground',
        variant === 'confirm-danger' && 'ob-escape__link--confirm ob-escape__link--confirm-danger',
        variant === 'keep' && 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

export default function ScanEscape({ onStartOver, onCancel, cancelCopy }: Props) {
  const [confirming, setConfirming] = useState<Confirming>(null);

  const cancel: EscapeCancelCopy = cancelCopy ?? {
    link: _t('onboarding.crawl.escape.cancel'),
    note: _t('onboarding.crawl.escape.cancelNote'),
    confirm: _t('onboarding.crawl.escape.cancelConfirm'),
    keep: _t('onboarding.crawl.escape.keep'),
  };

  if (!onStartOver && !onCancel) return null;

  const dismiss = () => setConfirming(null);

  return (
    <span className="ob-escape" data-testid="onboarding-scan-escape">
      <Separator />
      {confirming === null ? (
        <>
          {onStartOver && (
            <EscapeLink
              testId="onboarding-scan-escape-start-over"
              variant="chip"
              label={_t('onboarding.crawl.escape.startOver')}
              onClick={() => setConfirming('startOver')}
            />
          )}
          {onStartOver && onCancel && <Separator />}
          {onCancel && (
            <EscapeLink
              testId="onboarding-scan-escape-cancel"
              variant="quiet-danger"
              label={cancel.link}
              onClick={() => setConfirming('cancel')}
            />
          )}
        </>
      ) : (
        <span
          className="ob-escape__confirm"
          role="alertdialog"
          aria-label={confirming === 'startOver' ? _t('onboarding.crawl.escape.startOver') : cancel.link}
        >
          <span className="ob-escape__note text-muted-foreground">
            {confirming === 'startOver' ? _t('onboarding.crawl.escape.startOverNote') : cancel.note}
          </span>
          <span className="ob-escape__acts">
            <EscapeLink
              testId="onboarding-scan-escape-confirm"
              variant={confirming === 'cancel' ? 'confirm-danger' : 'confirm'}
              label={confirming === 'startOver' ? _t('onboarding.crawl.escape.startOverConfirm') : cancel.confirm}
              onClick={confirming === 'startOver' ? onStartOver ?? dismiss : onCancel ?? dismiss}
            />
            <EscapeLink
              testId="onboarding-scan-escape-keep"
              variant="keep"
              label={confirming === 'startOver' ? _t('onboarding.crawl.escape.keep') : cancel.keep}
              onClick={dismiss}
            />
          </span>
        </span>
      )}
    </span>
  );
}
